import axios from 'axios';

// Configuración base de la API
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Crear instancia de axios con configuración base
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Client-Type': 'web',
  },
});

// Interceptor para agregar el token a todas las requests
apiClient.interceptors.request.use(
  (config) => {
    config.headers = config.headers || {};
    config.headers['X-Client-Type'] = 'web';
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar respuestas y errores
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Solo redirigir en 401 si NO es una petición de login o registro
    const isAuthEndpoint = error.config?.url?.includes('/auth/login') || 
                          error.config?.url?.includes('/auth/register');
    
    if (error.response?.status === 401 && !isAuthEndpoint) {
      // Token expirado o inválido durante navegación normal
      console.log('Token expirado, redirigiendo al login...');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// Servicios de autenticación
export const authService = {
  // Login
  async login(credentials) {
    try {
      console.log('authService.login - enviando credenciales:', { identifier: credentials.email });
      
      const response = await apiClient.post('/auth/login', {
        identifier: credentials.email,
        password: credentials.password
      });

      console.log('authService.login - respuesta completa:', response);
      console.log('authService.login - respuesta data:', response.data);

      if (response.data.status === 'success') {
        const { token, user } = response.data;
        
        console.log('authService.login - login exitoso, token:', token);
        console.log('authService.login - login exitoso, user:', user);
        
        // Guardar token y datos del usuario
        localStorage.setItem('auth_token', token);
        localStorage.setItem('user_data', JSON.stringify(user));
        
        console.log('authService.login - datos guardados en localStorage');
        
        return {
          success: true,
          data: {
            token,
            user
          },
          message: response.data.message
        };
      }

      console.log('authService.login - login falló:', response.data.message);
      return {
        success: false,
        message: response.data.message || 'Error en el login'
      };
    } catch (error) {
      console.error('authService.login - error completo:', error);
      console.error('authService.login - error response:', error.response);
      console.error('authService.login - error response data:', error.response?.data);
      
      if (error.response?.data?.message) {
        return {
          success: false,
          message: error.response.data.message
        };
      }

      return {
        success: false,
        message: 'Error de conexión. Por favor, intenta nuevamente.'
      };
    }
  },

  // Logout
  async logout() {
    // Limpiar datos locales primero: no depender de que el servidor responda
    const token = localStorage.getItem('auth_token');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    if (!token) return;
    try {
      await apiClient.post('/auth/logout', null, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000,
      });
    } catch (error) {
      console.error('Error en logout:', error);
    }
  },

  // Obtener información del usuario actual
  async me() {
    try {
      const response = await apiClient.get('/auth/me');
      
      if (response.data.status === 'success') {
        // Actualizar datos del usuario en localStorage
        localStorage.setItem('user_data', JSON.stringify(response.data.user));
        
        return {
          success: true,
          data: response.data.user
        };
      }

      return {
        success: false,
        message: response.data.message || 'Error obteniendo datos del usuario'
      };
    } catch (error) {
      console.error('Error obteniendo datos del usuario:', error);
      return {
        success: false,
        message: 'Error de conexión'
      };
    }
  },

  // Refrescar token
  async refresh() {
    try {
      const response = await apiClient.post('/auth/refresh');
      
      if (response.data.status === 'success') {
        const { token } = response.data;
        localStorage.setItem('auth_token', token);
        
        return {
          success: true,
          data: { token }
        };
      }

      return {
        success: false,
        message: response.data.message || 'Error refrescando token'
      };
    } catch (error) {
      console.error('Error refrescando token:', error);
      return {
        success: false,
        message: 'Error de conexión'
      };
    }
  },

  // Verificar si el usuario está autenticado
  isAuthenticated() {
    const token = localStorage.getItem('auth_token');
    const userData = localStorage.getItem('user_data');
    return !!(token && userData);
  },

  // Obtener datos del usuario desde localStorage
  getUserData() {
    const userData = localStorage.getItem('user_data');
    return userData ? JSON.parse(userData) : null;
  },

  // Obtener token
  getToken() {
    return localStorage.getItem('auth_token');
  },

  // Métodos HTTP para uso general
  async get(url, config = {}) {
    try {
      const response = await apiClient.get(url, config);
      return response;
    } catch (error) {
      console.error(`GET ${url} error:`, error);
      throw error;
    }
  },

  async post(url, data = {}, config = {}) {
    try {
      const response = await apiClient.post(url, data, config);
      return response;
    } catch (error) {
      console.error(`POST ${url} error:`, error);
      throw error;
    }
  },

  async put(url, data = {}, config = {}) {
    try {
      const response = await apiClient.put(url, data, config);
      return response;
    } catch (error) {
      console.error(`PUT ${url} error:`, error);
      throw error;
    }
  },

  async delete(url, config = {}) {
    try {
      const response = await apiClient.delete(url, config);
      return response;
    } catch (error) {
      console.error(`DELETE ${url} error:`, error);
      throw error;
    }
  }
};

export default authService;